/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

// Messages
import messages from './messages/ReportAdditionalInfo_messages';

// Components
import Box, { Minifiable } from '../../components/Box';

const propTypes = {
  locale: PropTypes.object.isRequired,
  report: PropTypes.object.isRequired,
  showQuickCheck: PropTypes.func.isRequired,
};

// Workaround to make forceUpdate to work in the function component
function useForceUpdate() {
  const [value, setValue] = useState(true);
  return () => setValue(!value);
}

const ReportAdditionalInfo = ({ locale, report, showQuickCheck }) => {
  // Dont render component if there is no partners to show
  const partners = report.partners.filter(p => p.show_in_statement_partners);
  if (!partners.length) return null;

  const forceUpdate = useForceUpdate();
  const boxRef = useRef(null);

  const isMinified = () => (
    boxRef.current
      ? boxRef.current.state.minified
      : true
  );

  const renderPartner = partner => {
    const { country, identifier } = report;

    let urlLocalisations = {
      fi: partner.url_fi,
      sv: partner.url_sv,
      en: partner.url_en,
    };
    if (partner.slug === 'lk') {
      const params = { section: 'quickcheck' };
      const base = `/selvitys/${country}/${identifier}`;
      urlLocalisations = {
        fi: `${base}?${queryString.stringify({ ...params, lang: 'fi' })}`,
        sv: `${base}?${queryString.stringify({ ...params, lang: 'sv' })}`,
        en: `${base}?${queryString.stringify({ ...params, lang: 'en' })}`,
      };
    }

    const logoLocalisations = {
      fi: partner.logo_fi,
      sv: partner.logo_sv,
      en: partner.logo_en,
    };

    const descriptionLocalisations = {
      fi: partner.description_fi,
      sv: partner.description_sv,
      en: partner.description_en,
    };

    return (
      <Partner
        id={`company-partner-${partner.slug}`}
        key={partner.name}
        minified={isMinified()}
      >
        {partner.slug === 'lk' && (
          <PartnerLink onClick={showQuickCheck} minified={isMinified()}>
            <PartnerLogo
              minified={isMinified()}
              role="presentation"
              src={logoLocalisations[locale.lang]}
            />
          </PartnerLink>
        )}
        {partner.slug !== 'lk' && (
          <PartnerLink
            as="a"
            href={urlLocalisations[locale.lang]}
            rel="noopener noreferrer"
            target="_blank"
            minified={isMinified()}
          >
            <PartnerLogo
              minified={isMinified()}
              role="presentation"
              src={logoLocalisations[locale.lang]}
            />
          </PartnerLink>
        )}

        <Minifiable>
          <PartnerDescription>
            {descriptionLocalisations[locale.lang]}
          </PartnerDescription>
        </Minifiable>
      </Partner>
    );
  };

  return (
    <Box
      headerLabel={<FormattedMessage {...messages.title} />}
      onStateChange={forceUpdate}
      ref={boxRef}
    >
      <Partners minified={isMinified()}>
        {partners.map(renderPartner)}
      </Partners>

      <Minifiable>
        <PartnerInfo>
          <FormattedMessage {...messages.partnerInfo} />
        </PartnerInfo>
      </Minifiable>
    </Box>
  );
};

ReportAdditionalInfo.propTypes = propTypes;

const Partners = styled.div`
  align-items: center;
  display: flex;
  flex-direction: ${props => props.minified ? 'row' : 'column'};
  flex-wrap: wrap;
  margin: ${props => props.minified ? '-12px' : '-24px -12px'};
`;

const Partner = styled.div`
  border-bottom: ${props => props.minified
    ? 'none'
    : `1px solid ${props.theme['grey-normal']}`
  };
  display: ${props => props.minified ? 'block' : 'flex'};
  flex: 1;
  flex-direction: column;
  margin: 12px;
  min-width: 210px;
  padding: ${props => props.minified ? '0px' : '12px 0px'};
  width: ${props => props.minified ? 'auto' : '100%'};
  width: ${props => props.minified ? 'auto' : 'calc(100% - 24px)'};

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const PartnerDescription = styled.div`
  margin: 12px 0 0;

  @media (min-width: 640px) {
    margin: 0 0 0 12px;
  }
`;

const PartnerInfo = styled.p`
  background-color: ${props => props.theme.beige};
  border-radius: 6px;
  font-size: 10pt;
  line-height: 13pt;
  margin: 0px;
  padding: 12px;
`;

const PartnerLink = styled.div`
  display: block;
  max-width: 280px;
  max-height: 180px;
  flex-shrink: ${props => props.minified ? '1' : '0'};
`;

const PartnerLogo = styled.img`
  object-fit: contain;
  width: 100%;
  height: 100%;
`;

export default ReportAdditionalInfo;
